/**
 * Copyright 2022 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {h, Component} from 'preact';
import PropTypes from 'prop-types';
import config from '../../config';
import styles from '../styles/view.css';
import Video from './Video.jsx';
import SeekbarContainer from '../containers/SeekbarContainer.jsx';
import VideoControlsContainer from '../containers/VideoControlsContainer.jsx';
import StreamManager from '../containers/StreamManager.jsx';
import PlayButton from './controls/PlayButton.jsx';
import RewindButton from './controls/RewindButton.jsx';
import VolumeButtonAndSlider from './controls/VolumeButtonAndSlider.jsx';
import LatencyText from './controls/LatencyText.jsx';
import FullScreenButton from './controls/FullScreenButton.jsx';
import VideoQualityButton from './controls/VideoQualityButton.jsx';

const defaultViewConfig = {
  playButton: true,
  muteAudioButton: true,
  fullScreenButton: true,
  seekBar: false,
  rewindButton: false,
  streamLatency: false,
  streamQualities: false
};

class Player extends Component {
  constructor(props) {
    super(props);

    this.viewConfig = {
      ...defaultViewConfig,
      ...this.props.viewConfig
    };
    this.video = null;
    this.videoContainer = null;
    this.setVideoContainerSize = this.setVideoContainerSize.bind(this);
  }

  getDefaultElements() {
    const videoControls = [];
    const elements = [];

    if (this.viewConfig.playButton) {
      videoControls.push(<PlayButton key="PlayButton" paused={this.props.paused} play={this.props.play} video={this.video} />);
    }

    if (this.viewConfig.rewindButton) {
      videoControls.push(<RewindButton key="RewindButton" {...this.props} video={this.video} />);
    }

    if (this.viewConfig.muteAudioButton) {
      videoControls.push(<VolumeButtonAndSlider key="VolumeButtonAndSlider" {...this.props} alwaysShowVolumeSlider={config.environment.isMobile} video={this.video} />
      );
    }

    if (this.viewConfig.seekBar) {
      videoControls.push(<SeekbarContainer key="SeekbarContainer" {...this.props} video={this.video} />);
    }

    if (this.viewConfig.streamLatency) {
      videoControls.push(<LatencyText key="LatencyText" {...this.props} video={this.video} />);
    }

    if (this.viewConfig.streamQualities) {
      videoControls.push(<VideoQualityButton key="VideoQualityButton" {...this.props} video={this.video} />);
    }

    if (this.viewConfig.fullScreenButton) {
      videoControls.push(<FullScreenButton key="FullScreenButton" {...this.props} video={this.video} />);
    }

    elements.push(
      <VideoControlsContainer key="VideoControlsContainer" {...this.props} video={this.video}>
        {videoControls}
      </VideoControlsContainer>
    );

    return elements;
  }

  setVideo(element) {
    if (this.video) {
      return;
    }

    this.video = element;
    this.forceUpdate();

    this.setState({});
  }

  setVideoContainer(element) {
    this.videoContainer = element;
  }

  setVideoContainerSize() {
    if (this.videoContainer.parentNode) {
      const elementContainer = this.videoContainer.parentNode;

      this.videoContainer.style.height = elementContainer.getBoundingClientRect().height;
    }
  }

  componentDidMount() {
    this.setVideoContainerSize();

    window.addEventListener('resize', this.setVideoContainerSize);
  }

  componentWillUnmount() {
    this.video = null;
    this.videoContainer = null;
    window.removeEventListener('resize', this.setVideoContainerSize);
  }

  render() {
    return (
      <div id="videoColumn" className={styles.videoColumn}>
        <div id="videoContainer" className={styles.videoContainer} ref={(element) => this.setVideoContainer(element)}>
          <Video {...this.props} ref={(element) => this.setVideo(element)} />
          {this.getDefaultElements()}
          <StreamManager video={this.video} />
        </div>
      </div>
    );
  }
}

Player.propTypes = {
  id: PropTypes.string.isRequired,
  mediaStream: PropTypes.object,
  stream: PropTypes.object,
  className: PropTypes.string,
  playsInline: PropTypes.bool,
  autoPlay: PropTypes.bool,
  height: PropTypes.number,
  width: PropTypes.number,
  nativeControls: PropTypes.bool,
  defaultAudioEnabled: PropTypes.bool,
  defaultVideoEnabled: PropTypes.bool,
  storeLocalStateKey: PropTypes.string,
  renderer: PropTypes.object,

  // Video
  onStreamEnd: PropTypes.func,
  onVolumeChange: PropTypes.func,
  viewConfig: PropTypes.object.isRequired,

  // Play/Pause Button
  paused: PropTypes.bool,
  play: PropTypes.func,
  pause: PropTypes.func,

  // Mute Button
  isVideoEnabled: PropTypes.bool,
  canVideoStateBeChanged: PropTypes.bool,
  changeVideoEnabledState: PropTypes.func
};

export default Player;